import React, { useEffect, useMemo } from 'react'
import Dropdown from 'core/elements/dropdown'
import withProgress from 'core/components/progress/withProgress'
import useListAction from 'core/hooks/useListAction'
import { listTenantDefaultVolumeType, listVolumeTypes } from './actions'
import { useSelector } from 'react-redux'
import { defaultTenantVolumeTypesSelector, volumeTypesSelector } from './selectors'

const AsyncDropdown = withProgress(Dropdown, { inline: true })

export default function VolumeTypesPicklist({
  disabled = false,
  name = 'volumeTypes',
  label = 'Volume Type',
  includeNoneOption = false,
  filterFn = null,
  value = '',
  loading = false,
  onChange,
  overrideDefaultVolumeType = null,
  ...rest
}) {
  const { loading: loadingVolumeTypes } = useListAction(listVolumeTypes)
  const volumeTypes = useSelector(volumeTypesSelector)
  const { loading: loadingDefaultVolumeType } = useListAction(listTenantDefaultVolumeType)
  const defaultVolumeType = useSelector(defaultTenantVolumeTypesSelector)

  // For other tenants we need to fetch their default volume type and show it selected
  // So we override the defaultVolumeType with overrideDefaultVolumeType
  // Other tenants will not have overrideDefaultVolumeType
  const defaultVolumeTypeToShow = overrideDefaultVolumeType
    ? overrideDefaultVolumeType
    : defaultVolumeType

  const options = useMemo(() => {
    if (loadingDefaultVolumeType || loadingVolumeTypes) {
      return []
    }

    const volumeTypeOptions = volumeTypes.map((volumeType) => ({
      label:
        defaultVolumeTypeToShow.volume_type_id === volumeType.id
          ? `${volumeType.name || volumeType.id} (Default)`
          : volumeType.name || volumeType.id,
      value: volumeType.name || volumeType.id,
    }))
    const filteredOptions = filterFn ? filterFn(volumeTypeOptions, volumeTypes) : volumeTypeOptions
    return includeNoneOption ? [{ label: 'None', value: '' }, ...filteredOptions] : filteredOptions
  }, [volumeTypes, includeNoneOption, filterFn, defaultVolumeTypeToShow])

  useEffect(() => {
    if (loadingDefaultVolumeType || loadingVolumeTypes) {
      return
    }

    if (defaultVolumeTypeToShow?.volume_type_id && volumeTypes.length > 0) {
      const filteredType = volumeTypes.find(
        (type) => type.id === defaultVolumeTypeToShow.volume_type_id,
      )
      onChange((filteredType?.name || filteredType?.id) ?? '')
    } else {
      onChange(value ? value : options[0]?.value)
    }
  }, [defaultVolumeTypeToShow, volumeTypes, options])

  return (
    <AsyncDropdown
      {...rest}
      value={value}
      name={name}
      label={label}
      compact={false}
      disabled={disabled}
      loading={loading || loadingVolumeTypes || loadingDefaultVolumeType}
      items={options}
      onChange={onChange}
    />
  )
}
